import { useMemo, useCallback, useState } from "react";
import { orderBy } from "lodash";
import { Box, Button } from "@material-ui/core";
import DataListHeader from "components/DataListHeader";
import DataTable from "components/DataTable/DataTable";
import LoadingSpinner from "components/LoadingSpinner";
import PageComponent from "components/Page/PageComponent";
import ActivityTypesField from "features/activity/ActivityTypesField";
import { useGetAllActivityItemsQuery } from "features/activity/activitySlice";
import { useGetLotsQuery } from "features/lots/lotSlice";
import { ALL_TENANT_LOTS_OPTION_ID } from "components/SharedDropdowns/LotsDropDown/LotsDropDown";
import { useGetPricingByActivityTypeQuery } from "features/pricing/pricingSlice";
import { useGetVendorsQuery } from "features/vendor/vendorSlice";
import { useGetClientsQuery } from "features/clients/clientSlice";
import { arrayToObject } from "utils/object-util";
import { fieldTypes } from "services/filterService";
import useSortOptions from "hooks/useSortOptions";
import { PricingByClientStyle } from "../PricingByClient/PricingByClientStyle";
import { IconSearch } from "icons";
import { NO_ITEMS_MESSAGE, } from "../PricingByClient/PricingByClientConst";
import { getPricingCellStyle } from "../PricingByClient/pricingByClientUtils";
import PricingLotCell from "../PricingByClient/Components/PricingLotCell";
import PricingLegend from "../PricingByClient/Components/PricingLegend";
import PricingLotDetailsDialog from "../PricingByClient/Components/PricingLotDetailsDialog/PricingLotDetailsDialog";
import { buildActivityPricingMatrixData } from "./pricingByActivityUtils";
import ActivityPricingManagementDialog, { ACTIVITY_PRICING_DIALOG_MODES } from "../CreateActivityAndPricing/CreateActivityAndPricingDialog";
import BundleDialog, { BUNDLE_DIALOG_MODES } from "../CreateBundle/BundleDialog";
import { CREATE_BUNDLE_STEP_INDEX } from "../CreateBundle/createBundleConsts";
import { LOT_OPTIONS } from "../CreateActivityAndPricing/ActivityItemPricing/ActivityItemPricingConsts";
import { v4 as uuidv4 } from "uuid";
import { Alert } from "@mui/material";
import { ActivityItemPricingType } from "../PricingManagementConsts";

const PricingByActivity = () => {
  const classes = PricingByClientStyle();
  const [sortOptions, sortData] = useSortOptions();
  const [filters, setFilters] = useState({ lotIds: { value: [ALL_TENANT_LOTS_OPTION_ID] } });
  const [selectedPricingCell, setSelectedPricingCell] = useState(null);
  const [initialDialogTab, setInitialDialogTab] = useState(0);

  // Edit activity item state
  const [openDialog, setOpenDialog] = useState(false);
  const [activityItemIdToEdit, setActivityItemIdToEdit] = useState(null);

  // Edit bundle state
  const [openBundleEditDialog, setOpenBundleEditDialog] = useState(false);
  const [bundleIdToEdit, setBundleIdToEdit] = useState(null);
  const [bundlePrefill, setBundlePrefill] = useState(null);

  const onDropFilter = (value, prop) => {
    setFilters({ ...filters, [prop]: { value } });
  };

  const onActivityFilterChange = (rawId, { isBundle, jsonValue } = {}) => {
    setSearchParams(null);
    if (isBundle) {
      setFilters((prev) => ({ ...prev, bundleId: { value: rawId }, activityItemId: undefined, activityDropdownValue: jsonValue }));
    } else {
      setFilters((prev) => ({ ...prev, activityItemId: { value: rawId }, bundleId: undefined, activityDropdownValue: jsonValue }));
    }
  };

  const [searchParams, setSearchParams] = useState(null);
  const { data: activityPricing, isLoading: isInitialLoadingActivityPricing, isFetching: isFetchingActivityPricing } = useGetPricingByActivityTypeQuery(searchParams, { skip: !searchParams });

  let { data: lots, isLoading: isLoadingLots } = useGetLotsQuery();
  let { data: vendors, isLoading: isLoadingVendors } = useGetVendorsQuery();
  let { data: clients, isLoading: isLoadingClients } = useGetClientsQuery(null);
  let { data: activityItems, isLoading: isLoadingActivityItems } = useGetAllActivityItemsQuery();
  activityItems = activityItems || [];

  const vendorLookup = arrayToObject(vendors);
  const clientLookup = useMemo(() => arrayToObject(clients), [clients]);
  const activityItemsLookup = arrayToObject(activityItems || []);
  const lotsLookup = arrayToObject(lots);

  const selectedLotIds = filters?.lotIds?.value || [];

  // "All Tenant Lots" is stored as a single sentinel so the filter shows one chip.
  // By-Activity has no client filter, so it resolves to every tenant-owned lot.
  const effectiveLotIds = useMemo(() => {
    if (!selectedLotIds?.includes(ALL_TENANT_LOTS_OPTION_ID)) {
      return selectedLotIds;
    }
    return (lots ?? []).filter((lot) => lot.ownedByTenant).map((lot) => lot.id);
  }, [selectedLotIds, lots]);

  const executeSearch = () => {
    const activityId = filters?.activityItemId?.value;
    const bundleId = filters?.bundleId?.value;
    setSearchParams({ activityId, bundleId });
  };

  const handleSearch = () => {
    executeSearch();
  };

  // Edit activity item handlers
  const selectedActivityItemId = filters?.activityItemId?.value
    ? Number(filters?.activityItemId?.value)
    : null;
  const selectedActivityItem = activityItemsLookup[selectedActivityItemId];

  const selectedBundleId = filters?.bundleId?.value
    ? Number(filters?.bundleId?.value)
    : null;

  // Cell behavior is driven by what is actually displayed (the searched params),
  // which can differ from the current dropdown selection before a new search.
  const searchedBundleId = searchParams?.bundleId ?? null;
  const isBundleView = !!searchedBundleId;

  const onEditActivityItem = () => {
    if (!selectedActivityItem) return;
    setActivityItemIdToEdit(selectedActivityItem.id);
    setOpenDialog(true);
  };

  const onEditBundle = () => {
    if (!selectedBundleId) return;
    setBundlePrefill(null);
    setBundleIdToEdit(selectedBundleId);
    setOpenBundleEditDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setActivityItemIdToEdit(null);
  };

  const handleBundleEditClose = () => {
    setOpenBundleEditDialog(false);
    setBundleIdToEdit(null);
    setBundlePrefill(null);
  };

  // Pricing cell click handlers
  const handlePricingCellClick = (pricingData, row, lotId) => {
    if (!pricingData) {
      // State 3: no pricing for this client/lot — open the relevant edit dialog so
      // the user can add pricing for it.
      if (isBundleView) {
        // Bundles are edited through the bundle dialog. Open it directly on the
        // Pricing step with a new pricing item pre-seeded for this client + lot.
        setBundlePrefill({
          startStep: CREATE_BUNDLE_STEP_INDEX.PRICING,
          newPricingItem: {
            tempId: `${Date.now()}-${uuidv4()}`,
            isNew: true,
            clientId: row?.clientId != null ? Number(row.clientId) : null,
            lotOption: LOT_OPTIONS.SPECIFIC_LOTS,
            selectedLotIds: [lotId],
          },
        });
        setBundleIdToEdit(Number(searchedBundleId));
        setOpenBundleEditDialog(true);
      } else if (selectedActivityItemId) {
        setActivityItemIdToEdit(selectedActivityItemId);
        setOpenDialog(true);
      }
      return;
    }

    if (!isBundleView && pricingData.hasPricingCombinations && !pricingData.preferredVendorId) {
      // State 2: pricing combinations exist but no preferred vendor selected.
      // Bundles have no preferred-vendor tab, so this never applies to them.
      setInitialDialogTab(1);
    } else {
      // State 1: preferred vendor configured (or a bundle cell).
      setInitialDialogTab(0);
    }
    setSelectedPricingCell({ ...pricingData, pricingType: selectedActivityItem?.pricingType ?? ActivityItemPricingType.SIMPLE });
  };

  const handlePricingDialogClose = () => {
    setSelectedPricingCell(null);
    setInitialDialogTab(0);
  };

  const filterConfig = [
    {
      type: fieldTypes.COMPONENT,
      size: 4,
      component: (
        <ActivityTypesField
          onChange={onActivityFilterChange}
          value={filters?.activityDropdownValue}
          required={true}
          label="Activity"
          includeBundles={true}
        />
      ),
    },
    {
      type: fieldTypes.LOT,
      size: 5,
      title: "Lots",
      key: "lotIds",
      values: lots,
      multiple: true,
      allTenantLotsOption: true,
    },
    {
      type: fieldTypes.COMPONENT,
      size: 1,
      key: "searchButton",
      component: (
        <Box display="flex" alignItems="flex-end">
          <Button
            color="primary"
            variant="contained"
            onClick={handleSearch}
            disabled={!(filters?.activityItemId?.value || filters?.bundleId?.value)}
            startIcon={<IconSearch fill="white" />}
            style={{ height: "40px", marginTop: "7px" }}
          >
            Search
          </Button>
        </Box>
      ),
    },
  ];

  // Build matrix: clients as rows, lots as columns
  const { rows: matrixData, lotIdsFromData } = useMemo(() => {
    return buildActivityPricingMatrixData({
      activityPricing,
      selectedLotIds: effectiveLotIds,
      clientLookup,
      vendorLookup,
    });
  }, [activityPricing, effectiveLotIds, clientLookup, vendorLookup]);

  const sortedMatrixData = useMemo(() => {
    if (!sortOptions.columnToSort) return matrixData;
    return orderBy(
      matrixData,
      (row) => {
        const key = sortOptions.columnToSort;
        if (key.startsWith("lot_")) {
          return row[key]?.charge ?? null;
        }
        return row[key];
      },
      sortOptions.sortDirection
    );
  }, [matrixData, sortOptions]);

  // Derive lot columns: use selected lots if filtered, otherwise from data
  const lotsForColumns = useMemo(() => {
    const lotIdsForColumns = effectiveLotIds?.length > 0
      ? Array.from(new Set(effectiveLotIds))
      : Array.from(lotIdsFromData);

    return lotIdsForColumns
      .map((id) => lotsLookup[id])
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [effectiveLotIds, lotIdsFromData, lotsLookup]);

  const columns = useMemo(() => {
    return [
      {
        name: "Client",
        sortKey: "clientLabel",
        key: "clientLabel",
        width: 200,
        component: (row) => <div className={classes.activityCell}>{row?.clientLabel}</div>,
      },
      ...lotsForColumns.map((lot) => ({
        name: lot.name,
        sortKey: `lot_${lot.id}`,
        key: `lot_${lot.id}`,
        width: 120,
        cellStyle: (row) =>
          getPricingCellStyle({ row, lotId: lot.id }),
        component: (row) => {
          const pricingData = row[`lot_${lot.id}`];
          return (
            <PricingLotCell
              pricingData={pricingData}
              classes={classes}
              isBundle={isBundleView}
              onClick={() => handlePricingCellClick(pricingData, row, lot.id)}
            />
          );
        },
      })),
    ];
  }, [lotsForColumns, classes, isBundleView, searchedBundleId, selectedActivityItemId]);

  const mapExportData = useCallback(
    (row) => {
      const exportRow = { Client: row.clientLabel };
      lotsForColumns.forEach((lot) => {
        const cell = row[`lot_${lot.id}`];
        exportRow[lot.name] = cell ? cell.formattedCharge : "-";
      });
      return exportRow;
    },
    [lotsForColumns]
  );

  // First load (no data yet) replaces the table with a spinner. A background refetch
  // (e.g. after a save invalidates the cache) keeps the table mounted — RTK Query retains
  // the previous data for the same args — and only shows the overlay spinner while it updates.
  const isInitialLoading =
    isLoadingActivityItems ||
    isLoadingLots ||
    isInitialLoadingActivityPricing ||
    isLoadingVendors ||
    isLoadingClients;

  const isRefreshing = isFetchingActivityPricing;

  const showTable = searchParams !== null;

  return (
    <PageComponent
      header={
        <DataListHeader
          title="Pricing By Activity"
          data={matrixData}
          dropDownsConfig={filterConfig}
          filters={filters}
          onDropFilter={onDropFilter}
          actionButtons={
            selectedBundleId ? (
              <Button
                variant="outlined"
                color="primary"
                onClick={onEditBundle}
              >
                Edit Bundle
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                disabled={!selectedActivityItemId}
                onClick={onEditActivityItem}
              >
                Edit Activity Item
              </Button>
            )
          }
        />
      }
    >
      {!showTable ? (
        <div className={classes.emptyStateMessage}>{"Please select an activity to view pricing data"}</div>
      ) : (
        <>
          <LoadingSpinner loading={isInitialLoading || isRefreshing} />
          <PricingLegend />
          {!isInitialLoading && (
            <>
              {!selectedLotIds?.length && (
                <Alert severity="warning" className={classes.alert}>
                  {"Note: For better performance, consider filtering by specific lots if you have a large dataset."}
                </Alert>
              )}
              <DataTable
                columns={columns}
                rows={sortedMatrixData}
                rowIdentifier="id"
                onSort={sortData}
                mapExportData={mapExportData}
                sortDirection={sortOptions.sortDirection}
                columnToSort={sortOptions.columnToSort}
                noItemsMessage={NO_ITEMS_MESSAGE}
                headerStyle={{ paddingLeft: "1rem" }}
                isLoading={isRefreshing}
              />
            </>
          )}
        </>
      )}

      <PricingLotDetailsDialog
        open={!!selectedPricingCell}
        onClose={handlePricingDialogClose}
        pricingData={selectedPricingCell}
        initialTab={initialDialogTab}
      />

      <ActivityPricingManagementDialog
        open={openDialog}
        activityItemId={activityItemIdToEdit}
        handleClose={handleClose}
        mode={ACTIVITY_PRICING_DIALOG_MODES.EDIT}
      />

      <BundleDialog
        open={openBundleEditDialog}
        bundleId={bundleIdToEdit}
        handleClose={handleBundleEditClose}
        mode={BUNDLE_DIALOG_MODES.EDIT}
        prefill={bundlePrefill}
      />
    </PageComponent>
  );
};

export default PricingByActivity;
